import { Link } from "@/i18n/routing";
import { cn } from "@/lib/utils";
import { type VariantProps } from "class-variance-authority";
import * as React from "react";
import { JSXElementConstructor } from "react";
import { buttonVariants, iconPadding } from "../button";

export interface LinkButtonProps
  extends Omit<React.ComponentProps<typeof Link>, "className" | "children">,
    VariantProps<typeof buttonVariants> {
  className?: string;
  children?: React.ReactNode;
}

/**
 * LinkButton Component
 *
 * A styled anchor link that applies button variants and hierarchies.
 * Supports different sizes, states, and icon-only configurations while
 * maintaining consistent design with the Button component.
 */
const LinkButton = React.forwardRef<HTMLAnchorElement, LinkButtonProps>(
  ({ className, variant, hierarchy, size, children, href, ...props }, ref) => {
    let hrefModified = href;

    // Fallback to '#' if href is not a valid string
    if (typeof href !== "string") {
      hrefModified = "#";
    }
    const childArray = React.Children.toArray(children);

    // Detect if the button contains only an icon element
    const isIconOnly =
      childArray?.length === 1 &&
      React.isValidElement(childArray[0]) &&
      (childArray[0]?.type as JSXElementConstructor<object>)?.name === "Icon";

    return (
      <a
        href={hrefModified as string}
        className={cn(
          buttonVariants({ variant, hierarchy, size, className }),
          isIconOnly && iconPadding[size as "xs" | "sm" | "md" | "lg" | "xl"],
        )}
        ref={ref}
        {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {children}
      </a>
    );
  },
);

LinkButton.displayName = "LinkButton";
export { LinkButton };
